import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { exportToCSV } from '../utils/csvExport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Users,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  CircleNotch,
  Download,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = { customer_code: '', name: '', phone: '', email: '', address: '' };

export default function Customers() {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const canAddCustomer = hasPermission('add_customer');
  const canEditCustomer = hasPermission('edit_customer');

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/customers?${params}`, { withCredentials: true });
      setCustomers(res.data);
    } catch (error) { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        customer_code: customer.customer_code || '', name: customer.name || '',
        phone: customer.phone || '', email: customer.email || '', address: customer.address || ''
      });
    } else {
      setEditingCustomer(null);
      setFormData(initialForm);
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => { setShowDialog(false); setEditingCustomer(null); setFormData(initialForm); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCustomer) {
        await axios.put(`${API_URL}/api/customers/${editingCustomer.id}`, formData, { withCredentials: true });
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API_URL}/api/customers`, formData, { withCredentials: true });
        toast.success('Customer created successfully');
      }
      handleCloseDialog();
      fetchCustomers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save customer'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API_URL}/api/customers/${customerId}`, { withCredentials: true });
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (error) { toast.error('Failed to delete customer'); }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('customers')}</h1>
          <p className="text-[var(--dm-text-muted)] mt-1">{t('manageCustomerInfo')}</p>
        </div>
        {canAddCustomer && (
          <div className="flex gap-2">
            <Button onClick={() => exportToCSV(customers.map(c => ({
              Name: c.name, Email: c.email, Phone: c.phone, Address: c.address, Notes: c.notes
            })), 'customers')} data-testid="export-customers-btn"
              className="bg-[var(--dm-bg-card-hover)] text-[var(--text)] font-semibold hover:bg-[var(--dm-border)] rounded-xl h-11 border border-[var(--dm-border)]">
              <Download size={20} className="mr-2" />CSV
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="add-customer-btn"
              className="bg-black text-white font-semibold hover:bg-black/80 rounded-xl h-11 border border-[var(--dm-border)]">
              <Plus size={20} className="mr-2" />{t('addCustomer')}
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--dm-text-muted)]" />
        <Input placeholder={t('searchCustomers')} value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="customer-search" className="pl-10 bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-11" />
      </div>

      <div className="bg-[var(--card)] border border-[var(--dm-border)] overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full table-dm">
            <thead>
              <tr>
                <th className="text-left p-4">{t('customerCode')}</th>
                <th className="text-left p-4">{t('name')}</th>
                <th className="text-left p-4">{t('phone')}</th>
                <th className="text-left p-4">{t('email')}</th>
                {canEditCustomer && <th className="text-center p-4">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-8 text-center"><CircleNotch size={24} className="animate-spin text-[var(--primary)] mx-auto" /></td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-[var(--dm-text-muted)]"><Users size={48} className="mx-auto mb-2 opacity-50" /><p>No customers found</p></td></tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <td className="p-4 font-mono text-[var(--primary)]">{customer.customer_code}</td>
                    <td className="p-4 text-[var(--text)] font-medium">{customer.name}</td>
                    <td className="p-4 text-[var(--dm-text-muted)]">{customer.phone || '-'}</td>
                    <td className="p-4 text-[var(--dm-text-muted)]">{customer.email || '-'}</td>
                    {canEditCustomer && (
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => handleOpenDialog(customer)} data-testid={`edit-customer-${customer.id}`} className="p-2 text-[var(--dm-text-muted)] hover:text-[var(--primary)] transition-colors"><PencilSimple size={18} /></button>
                          <button onClick={() => handleDelete(customer.id)} data-testid={`delete-customer-${customer.id}`} className="p-2 text-[var(--dm-text-muted)] hover:text-[#EF4444] transition-colors"><Trash size={18} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
              {editingCustomer ? t('editCustomer') : t('addNewCustomer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <div>
              <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('customerCode')}</Label>
              <Input value={formData.customer_code} onChange={(e) => setFormData({ ...formData, customer_code: e.target.value })}
                data-testid="customer-code-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" placeholder="CUST001" />
            </div>
            <div>
              <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('name')} *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required data-testid="customer-name-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('phone')}</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="customer-phone-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
              </div>
              <div>
                <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('email')}</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="customer-email-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
              </div>
            </div>
            <div>
              <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('address')}</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="customer-address-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--dm-border)]">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-[var(--dm-border)] text-[var(--dm-text-muted)] rounded-xl">{t('cancel')}</Button>
              <Button type="submit" disabled={saving} data-testid="save-customer-btn"
                className="bg-black text-white font-semibold rounded-xl border border-[var(--dm-border)]">
                {saving ? <CircleNotch size={20} className="animate-spin" /> : t('saveCustomer')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
